import { type CreatePatientInput, type LabTest, type Panel } from '#shared/types';
import { Banner } from '#client/components/ui/Banner';
import { Card } from '#client/components/ui/Card';
import { Checkbox } from '#client/components/ui/Checkbox';
import { CheckboxPicker } from '#client/components/ui/CheckboxPicker';
import { NumberField } from '#client/components/ui/NumberField';
import { Select } from '#client/components/ui/Select';
import { TextArea } from '#client/components/ui/TextArea';
import { TextField } from '#client/components/ui/TextField';
import { formatMoney } from '#client/lib/format';
import type { SetPatientValue } from './patient-form.logic';
import { ReturningPatientNotice } from './ReturningPatientNotice';
import type { PatientMatch } from './usePatientLookup';
import { GENDER_OPTIONS, MARITAL_STATUS_OPTIONS } from '#client/lib/form-choices';

interface FieldsProps {
  values: CreatePatientInput;
  onFieldChange: SetPatientValue;
  errors: Record<string, string>;
}

/** Who the patient is and how to reach them, whichever form registers the visit. */
export type DemographicValues = Pick<
  CreatePatientInput,
  | 'gender'
  | 'age_year'
  | 'age_month'
  | 'age_days'
  | 'married_status'
  | 'phone_number'
  | 'email'
  | 'cnic_number'
  | 'refer_by'
>;

interface DemographicFieldsProps {
  values: DemographicValues;
  onFieldsChange: (changes: Partial<DemographicValues>) => void;
  errors: Partial<Record<string, string>>;
}

export function PatientIdentityCard({
  values,
  onFieldChange,
  onFieldsChange,
  errors,
  returning,
}: FieldsProps & {
  onFieldsChange: DemographicFieldsProps['onFieldsChange'];
  returning: PatientMatch | null;
}) {
  return (
    <Card title="Patient">
      {returning && <ReturningPatientNotice match={returning} />}
      <div className="c-form-grid">
        <TextField
          label="Name"
          value={values.name}
          error={errors.name}
          onChange={(event) => onFieldChange('name', event.target.value)}
          required
        />
        <DemographicFields values={values} onFieldsChange={onFieldsChange} errors={errors} />
      </div>

      {/* Asked while the patient is standing there, which is the only
          moment it can honestly be asked. The server stamps who
          recorded it and when; the box is the only part the form
          sends. */}
      <Checkbox
        label="Patient agrees to receive reports on WhatsApp"
        checked={values.whatsapp_consent}
        onChange={(event) => onFieldChange('whatsapp_consent', event.target.checked)}
      />
      <p className="c-field__help">
        Reports are sent automatically as a PDF to the number above once every test is complete.
        WhatsApp is not a secure medical channel — anyone holding the phone can open the report.
      </p>
    </Card>
  );
}

/**
 * Age, sex and contact details, rendered as a fragment inside the caller's
 * grid. Shared with registration from a test request, which takes the name from
 * the request itself.
 */
export function DemographicFields({ values, onFieldsChange, errors }: DemographicFieldsProps) {
  return (
    <>
      <PersonFields values={values} onFieldsChange={onFieldsChange} errors={errors} />
      <ContactFields values={values} onFieldsChange={onFieldsChange} errors={errors} />
    </>
  );
}

function PersonFields({ values, onFieldsChange, errors }: DemographicFieldsProps) {
  return (
    <>
      <Select
        label="Gender"
        options={GENDER_OPTIONS}
        value={values.gender}
        error={errors.gender}
        onChange={(choice) => onFieldsChange({ gender: choice })}
        required
      />
      <NumberField
        label="Age (years)"
        min={0}
        max={150}
        value={values.age_year}
        error={errors.age_year}
        onChange={(years) => onFieldsChange({ age_year: years })}
        required
      />
      <NumberField
        label="Age (months)"
        min={0}
        max={11}
        value={values.age_month}
        error={errors.age_month}
        onChange={(months) => onFieldsChange({ age_month: months })}
      />
      <NumberField
        label="Age (days)"
        min={0}
        max={31}
        value={values.age_days}
        error={errors.age_days}
        onChange={(days) => onFieldsChange({ age_days: days })}
      />
      <Select
        label="Marital status"
        options={MARITAL_STATUS_OPTIONS}
        value={values.married_status}
        error={errors.married_status}
        onChange={(choice) => onFieldsChange({ married_status: choice })}
        required
      />
    </>
  );
}

/** How to reach them and who sent them. */
function ContactFields({ values, onFieldsChange, errors }: DemographicFieldsProps) {
  return (
    <>
      <TextField
        label="Phone number"
        value={values.phone_number ?? ''}
        error={errors.phone_number}
        onChange={(event) => onFieldsChange({ phone_number: event.target.value })}
        helpText="A number seen before registers this visit under the same MR number"
      />
      <TextField
        label="Email"
        type="email"
        value={values.email ?? ''}
        error={errors.email}
        onChange={(event) => onFieldsChange({ email: event.target.value })}
      />
      <TextField
        label="CNIC number"
        value={values.cnic_number ?? ''}
        error={errors.cnic_number}
        onChange={(event) => onFieldsChange({ cnic_number: event.target.value })}
      />
      <TextField
        label="Referred by"
        value={values.refer_by ?? ''}
        error={errors.refer_by}
        onChange={(event) => onFieldsChange({ refer_by: event.target.value })}
      />
    </>
  );
}

export function PatientNoteCard({ values, onFieldChange, errors }: FieldsProps) {
  return (
    <Card title="Note">
      <TextArea
        label="Note"
        value={values.note ?? ''}
        error={errors.note}
        onChange={(event) => onFieldChange('note', event.target.value)}
        rows={3}
      />
    </Card>
  );
}

interface PickerCardProps<TItem> {
  items: TItem[];
  loading: boolean;
  loadError: string | null | undefined;
  selectedIds: number[];
  onChange: (ids: number[]) => void;
  error: string | undefined;
}

export function LabTestsCard(props: PickerCardProps<LabTest>) {
  return (
    <Card title="Lab tests" subtitle="Each selected test opens a report">
      {props.loadError && <Banner tone="critical">{props.loadError}</Banner>}
      <CheckboxPicker
        items={props.items}
        selectedIds={props.selectedIds}
        onChange={props.onChange}
        label={(test) => `${test.name} (${test.code}) — ${formatMoney(test.price)}`}
        searchText={(test) => test.code}
        loading={props.loading}
        searchPlaceholder="Search tests by name or code"
        emptyState="No lab tests defined yet"
        error={props.error}
      />
    </Card>
  );
}

export function PanelsCard(props: PickerCardProps<Panel>) {
  return (
    <Card title="Panels">
      {props.loadError && <Banner tone="critical">{props.loadError}</Banner>}
      <CheckboxPicker
        items={props.items}
        selectedIds={props.selectedIds}
        onChange={props.onChange}
        label={(panel) => `${panel.name} (${panel.code})`}
        searchText={(panel) => panel.code}
        loading={props.loading}
        searchPlaceholder="Search panels"
        emptyState="No panels defined yet"
        error={props.error}
      />
    </Card>
  );
}
